import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/security';
import { releaseNoteApi, wikiArticleApi } from '../utils/api';
import { Search, X } from 'lucide-react';
import BinaryWordmark from './BinaryWordmark';

const Navbar: React.FC = () => {
    const { isAuthenticated, userInfo, isSystemAdmin, canRead, logout } = useAuth();
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const location = useLocation();

    // Global search state
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Array<{ type: string; id: string; title: string; subtitle: string; icon: string; link: string }>>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close search on route change
    useEffect(() => {
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
    }, [location.pathname]);

    // Debounced search
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchResults([]);
            return;
        }

        const timer = setTimeout(async () => {
            setSearchLoading(true);
            const [releaseResult, wikiResult] = await Promise.all([
                releaseNoteApi.search(searchQuery.trim()),
                wikiArticleApi.search(searchQuery.trim()),
            ]);

            const results: Array<{ type: string; id: string; title: string; subtitle: string; icon: string; link: string }> = [];

            if (!releaseResult.error && releaseResult.data) {
                for (const note of releaseResult.data) {
                    const releaseVersion = (note as any).ReleaseVersion || '';
                    const jiraKey = note.JiraKey || note.jiraKey || '';
                    const type = (note.Type ?? note.type) || 0;
                    const typeLabel = type === 1 ? 'Bug' : type === 2 ? 'Improvement' : type === 3 ? 'Feature' : '';
                    results.push({
                        type: 'release-note',
                        id: note.Id || note.id,
                        title: note.Title || note.title || 'Untitled',
                        subtitle: `${releaseVersion}${jiraKey ? ` · ${jiraKey}` : ''}${typeLabel ? ` · ${typeLabel}` : ''}`,
                        icon: type === 1 ? '🐛' : type === 2 ? '↑' : type === 3 ? '+' : '📄',
                        link: `/releases?noteId=${note.Id || note.id}`,
                    });
                }
            }

            if (!wikiResult.error && wikiResult.data) {
                for (const article of wikiResult.data) {
                    results.push({
                        type: 'wiki-article',
                        id: article.id || article.Id,
                        title: article.title || article.Title || 'Untitled',
                        subtitle: article.tocName || article.tocDescription || 'Documentation',
                        icon: '📖',
                        link: `/wiki/article/${article.id || article.Id}`,
                    });
                }
            }

            setSearchResults(results);
            setSearchLoading(false);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Focus input when search opens
    useEffect(() => {
        if (searchOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [searchOpen]);

    // Keyboard shortcut: Cmd/Ctrl + K to open search
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setSearchOpen(prev => !prev);
                if (!searchOpen) {
                    setSearchQuery('');
                    setSearchResults([]);
                }
            }
            // Escape to close search
            if (e.key === 'Escape' && searchOpen) {
                setSearchOpen(false);
                setSearchQuery('');
                setSearchResults([]);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [searchOpen]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            setSearchOpen(false);
            setSearchResults([]);
            window.location.href = `/search/${encodeURIComponent(searchQuery.trim())}`;
        }
    };

    const handleResultClick = (link: string) => {
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
        window.location.href = link;
    };

    // Modelled on the header at binary.ax: a graphite pill with the logo on a
    // red tile at its left end and a solid red rule along the bottom, and a
    // separate red block at the far right where the site puts "Get in touch".
    // The pill keeps its graphite and white in both themes, as the site's does.
    return (
        <header className="bx-toolbar no-print">
            <div className="bx-wrap">
                <div className="bx-toolbar-row">
                    <div className="bx-nav">
                        <Link to="/" className="bx-nav-logo" aria-label="Binary — BIKI home">
                            <BinaryWordmark />
                        </Link>

                        {/* Where the site sets its "Feed Dynamics" sub-brand. */}
                        <Link to="/" className="bx-nav-mark">
                            BIKI
                        </Link>

                        <nav className="bx-nav-links" aria-label="Main">
                            {canRead('releaseNotes') && (
                                <Link
                                    to="/releases"
                                    className={`bx-nav-link ${location.pathname === '/releases' ? 'is-active' : ''}`}
                                    aria-current={location.pathname === '/releases' ? 'page' : undefined}
                                >
                                    Release Notes
                                </Link>
                            )}
                            {canRead('wiki') && (
                                <Link
                                    to="/wiki"
                                    className={`bx-nav-link ${location.pathname.startsWith('/wiki') ? 'is-active' : ''}`}
                                    aria-current={location.pathname.startsWith('/wiki') ? 'page' : undefined}
                                >
                                    Documentation
                                </Link>
                            )}
                        </nav>

                        {/* Global Search */}
                        <div className="bx-nav-search relative" ref={searchRef}>
                            {/* Search trigger */}
                            <button
                                onClick={() => setSearchOpen(!searchOpen)}
                                className={`bx-nav-search-btn ${searchOpen ? 'is-open' : ''}`}
                            >
                                <Search className="w-4 h-4 shrink-0" />
                                <span className="truncate">Search...</span>
                                <kbd className="hidden sm:inline-flex">⌘K</kbd>
                            </button>

                        {/* Search dropdown */}
                        {searchOpen && (
                            <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-[540px] bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
                                <form onSubmit={handleSearchSubmit} className="border-b border-slate-100">
                                    <div className="flex items-center gap-2 px-4 py-3">
                                        <Search className="w-5 h-5 text-slate-400 shrink-0" />
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Search release notes & documentation..."
                                            className="flex-1 text-sm outline-none bg-transparent text-slate-700 placeholder-slate-400"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearchOpen(false);
                                                setSearchQuery('');
                                                setSearchResults([]);
                                            }}
                                            className="text-slate-400 hover:text-slate-600 transition-colors"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                </form>

                                {/* Live results */}
                                {searchQuery.trim() && (
                                    <div className="max-h-[400px] overflow-y-auto">
                                        {searchLoading ? (
                                            <div className="flex items-center justify-center py-8">
                                                <div className="spinner w-5 h-5"></div>
                                            </div>
                                        ) : searchResults.length > 0 ? (
                                            <div className="py-1">
                                                {searchResults.map((result, index) => (
                                                    <button
                                                        key={`${result.type}-${result.id}-${index}`}
                                                        onClick={() => handleResultClick(result.link)}
                                                        className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-start gap-3"
                                                    >
                                                        <span className="text-lg shrink-0 mt-0.5">{result.icon}</span>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-sm font-medium text-slate-800 truncate">{result.title}</p>
                                                            <p className="text-xs text-slate-500 truncate">{result.subtitle}</p>
                                                        </div>
                                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${
                                                            result.type === 'release-note'
                                                                ? 'bg-purple-50 text-purple-600'
                                                                : 'bg-blue-50 text-blue-600'
                                                        }`}>
                                                            {result.type === 'release-note' ? 'Release' : 'Docs'}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-8">
                                                <Search className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                                <p className="text-sm text-slate-400">No results found</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Empty state */}
                                {!searchQuery.trim() && (
                                    <div className="text-center py-8">
                                        <Search className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                        <p className="text-sm text-slate-400">Type to search release notes & documentation</p>
                                    </div>
                                )}
                            </div>
                        )}
                        </div>
                    </div>

                    {/* The right-hand block, in place of the site's "Get in touch". */}
                    <div className="flex items-stretch gap-3 relative" ref={menuRef}>
                        {isAuthenticated && userInfo ? (
                            <>
                                <button
                                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                                    className="bx-user"
                                    aria-haspopup="menu"
                                    aria-expanded={userMenuOpen}
                                >
                                    <span className="hidden md:block min-w-0">
                                        <span className="bx-user-name block truncate">{userInfo.name}</span>
                                        <span className="bx-user-email block truncate">{userInfo.email}</span>
                                    </span>
                                    <span className="bx-user-avatar">
                                        {userInfo.avatar ? (
                                            <img src={userInfo.avatar} alt="" />
                                        ) : (
                                            userInfo.name.charAt(0).toUpperCase()
                                        )}
                                    </span>
                                </button>
                                {userMenuOpen && (
                                    <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50">
                                        {/* The pill drops its links below 768px, so they live here
                                            instead — otherwise there is no navigation on a phone. */}
                                        <div className="md:hidden border-b border-slate-100 py-1">
                                            {canRead('releaseNotes') && <Link
                                                to="/releases"
                                                className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                                                onClick={() => setUserMenuOpen(false)}
                                            >
                                                Release Notes
                                            </Link>}
                                            {canRead('wiki') && <Link
                                                to="/wiki"
                                                className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                                                onClick={() => setUserMenuOpen(false)}
                                            >
                                                Documentation
                                            </Link>}
                                        </div>
                                        {isSystemAdmin && (
                                            <Link
                                                to="/admin"
                                                className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                                                onClick={() => setUserMenuOpen(false)}
                                            >
                                                Admin
                                            </Link>
                                        )}
                                        <button
                                            onClick={() => { logout(); setUserMenuOpen(false); }}
                                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-slate-100"
                                        >
                                            Sign out
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <Link to="/login" className="bx-user">
                                <span className="bx-user-name">Sign in</span>
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Navbar;
